import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { checkPermission } from '@/utils/permission';
import DynamicTable from '@/components/DynamicTable';
import { isValidDate, renderCustomerServiceStatus, renderServiceGroup } from '@/utils/helpers';
import { Button, Col, Input, message, Modal, Row, Space, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { fetchCustomer } from '@/redux/actions/customerActions';
import { getReceipt } from '@/redux/actions/receiptActions';
import HtmlParser from 'react-html-parser';
import { getSpecificService, removeService } from '@/redux/actions/customerServicesActions';
import { recalculateCommission } from '@/redux/actions/commissionActions';
import CustomerReceiptsTable from '@/components/customers/CustomerReceiptsTable';
import CreatedAtCell from '@/components/CreatedAtCell';
import MedicalStaffBlock from '@/components/MedicalStaffBlock';
import CustomerServiceForm from '@/components/customer-services/CustomerServiceForm';
import ServicePriceCell from '@/components/ServicePriceCell';
import CustomerServicesDetail from '@/components/customer-services/CustomerServicesDetail';
import CustomerServiceCommissionSetupForm from '@/components/customer-services/CustomerServiceCommissionSetupForm';
import { getCustomerServiceDoctorRoleGroups } from '@/components/customer-services/customerServiceDoctorRoleHelpers';
import ReceiptDetail from '@/routes/receipts/ReceiptDetail';

const { confirm } = Modal;

const PurchasedServices = (props) => {
    const { customer = {} } = props;
    const services = customer.purchased_services || [];

    const dispatch = useDispatch();
    const { id } = useParams();

    const allowCreate = checkPermission('customer_services', 'create');
    const allowDetail = checkPermission('customer_services', 'detail');
    const allowUpdate = checkPermission('customer_services', 'update');
    const allowDelete = checkPermission('customer_services', 'delete');
    const canViewFinance = checkPermission('customer', 'view_finance');
    const allowCommissionSetupDetail = checkPermission('customer_service_commission_setup', 'detail');
    const allowCommissionSetupUpdate = checkPermission('customer_service_commission_setup', 'update');
    const allowCommissionRecalculate = checkPermission('commission_result', 'calculate');

    const [isLoading, setIsLoading] = useState(false);
    const [isOpenCreateForm, setIsOpenCreateForm] = useState(false);
    const [isOpenDetail, setIsOpenDetail] = useState(false);
    const [editServiceId, setEditServiceId] = useState(null);
    const [commissionServiceId, setCommissionServiceId] = useState(null);
    const [currentUsedService, setCurrentUsedService] = useState(null);
    const [isOpenReceiptDetail, setIsOpenReceiptDetail] = useState(false);
    const [receiptDetailLoading, setReceiptDetailLoading] = useState(false);
    const [currentReceiptId, setCurrentReceiptId] = useState(null);
    const [currentReceipt, setCurrentReceipt] = useState(null);

    const closeForm = () => {
        setIsOpenCreateForm(false);
        setEditServiceId(null);
    };

    const closeDetail = () => {
        setIsOpenDetail(false);
        setCurrentUsedService(null);
    };

    const openCommissionSetup = (service = null) => {
        const serviceId = service?.id || currentUsedService?.id;
        if (serviceId) setCommissionServiceId(Number(serviceId));
    };
    const closeCommissionSetup = () => {
        setCommissionServiceId(null);
    };

    const openAddForm = () => {
        setCurrentUsedService(null);
        setIsOpenCreateForm(true);
    };

    const openEditForm = (serviceId) => setEditServiceId(Number(serviceId));

    const onDetail = async (serviceId) => {
        try {
            const serviceRes = await dispatch(getSpecificService(serviceId));
            setCurrentUsedService(serviceRes || null);
            setIsOpenDetail(true);
        } catch (error) {
            console.error('Lỗi khi tải dịch vụ:', error);
        }
    };

    const handleReloadData = async () => {
        try {
            setIsLoading(true);
            await dispatch(fetchCustomer(id));
        } catch (error) {
            console.error('Failed to reload data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadReceiptDetail = async (receiptId, reloadCustomer = false) => {
        if (!receiptId || !checkPermission('receipt', 'detail')) return null;
        setReceiptDetailLoading(true);
        try {
            const requests = [dispatch(getReceipt(receiptId))];
            if (reloadCustomer) requests.push(dispatch(fetchCustomer(id)));

            const [receipt] = await Promise.all(requests);
            setCurrentReceipt(receipt || null);
            return receipt;
        } catch (error) {
            console.error('Lỗi khi tải phiếu thu:', error);
            setCurrentReceipt(null);
            return null;
        } finally {
            setReceiptDetailLoading(false);
        }
    };

    const openReceiptDetail = receiptId => {
        setCurrentReceiptId(Number(receiptId));
        setCurrentReceipt(null);
        setIsOpenReceiptDetail(true);
        return loadReceiptDetail(receiptId);
    };

    const closeReceiptDetail = () => {
        setIsOpenReceiptDetail(false);
        setCurrentReceiptId(null);
        setCurrentReceipt(null);
    };

    const reloadReceiptDetail = () => loadReceiptDetail(currentReceiptId, true);

    const handleServiceSaved = async (serviceId) => {
        await handleReloadData();
        if (isOpenDetail && Number(currentUsedService?.id) === Number(serviceId)) {
            const serviceRes = await dispatch(getSpecificService(serviceId));
            setCurrentUsedService(serviceRes || null);
        }
    };

    const handleStaffChanged = async () => {
        if (!currentUsedService?.id) return;
        const serviceRes = await dispatch(getSpecificService(currentUsedService.id));
        setCurrentUsedService(serviceRes || null);
        await handleReloadData();
    };

    const recalculateFromDetail = async (serviceId) => {
        const result = await dispatch(recalculateCommission(serviceId));
        await handleReloadData();
        const serviceRes = await dispatch(getSpecificService(serviceId));
        setCurrentUsedService(serviceRes || null);
        return result;
    };

    const openAlert = (ids) => {
        let reason = '';
        confirm({
            title: 'Hủy dịch vụ và hoàn nguồn thanh toán',
            content: <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text>Hệ thống sẽ tự động đảo phần tiền đã phân bổ, chuyển khoản còn lại về số dư khách và cập nhật hoa hồng liên quan. Lịch sử cũ vẫn được giữ để tra cứu.</Typography.Text>
                <Tooltip title="Nêu căn cứ hủy dịch vụ để lưu cùng lịch sử tài chính, hoàn nguồn và audit.">
                    <Input.TextArea rows={3} maxLength={1000} placeholder="Nhập lý do hủy dịch vụ" onChange={(event) => { reason = event.target.value; }} />
                </Tooltip>
            </Space>,
            okText: 'Xác nhận hủy',
            cancelText: 'Huỷ',
            async onOk() {
                if (!reason.trim()) {
                    message.warning('Vui lòng nhập lý do hủy dịch vụ.');
                    return Promise.reject();
                }
                try {
                    await dispatch(removeService(ids, reason.trim()));
                    await handleReloadData();
                    Modal.destroyAll();
                } catch (error) {
                    console.error('Lỗi khi xoá dịch vụ:', error);
                }
            },
        });
    };

    const columns = [
        {
            title: 'Tên dịch vụ',
            dataIndex: 'service_name',
            key: 'service_name',
            width: '200px',
            sorter: false,
            render: (text, record) => {
                const serviceName = text || record?.service?.name || record?.name;

                if (!serviceName) {
                    return <Tag>-</Tag>;
                }

                const isTransferred = !!record.is_transferred;
                const serviceGroup = record?.service?.service_group;

                const serviceContent = (
                    <div>
                        <div
                            className="color-primary item-action-btn"
                            onClick={() => allowDetail && onDetail(record.id)}
                        >
                            {serviceName}
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                flexWrap: 'wrap',
                                marginTop: 3,
                            }}
                        >
                            {serviceGroup && renderServiceGroup(serviceGroup)}

                            <span
                                style={{
                                    fontSize: 11,
                                    color: '#aaa',
                                    fontWeight: 400,
                                }}
                            >
                                ID: {record?.id} - Service ID: {record.service_id}
                            </span>
                        </div>
                    </div>
                );

                return (
                    <>
                        {allowDetail ? (
                            <Tooltip title="Xem chi tiết dịch vụ" placement="right">
                                {serviceContent}
                            </Tooltip>
                        ) : (
                            serviceContent
                        )}

                        {record.converted_from_service_name && (
                            <Tooltip title={`Được chuyển từ: ${record.converted_from_service_name}`}>
                                <Tag color="green" className="ml-2">
                                    Được chuyển từ: {record.converted_from_service_name}
                                </Tag>
                            </Tooltip>
                        )}

                        {isTransferred && record.converted_to_service_name && (
                            <Tooltip title={`Đã chuyển sang: ${record.converted_to_service_name}`}>
                                <Tag color="red" className="ml-2">
                                    Đã chuyển sang: {record.converted_to_service_name}
                                </Tag>
                            </Tooltip>
                        )}
                    </>
                );
            },
        },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            key: 'note',
            sorter: false,
            width: '200px',
            render: (text) => <>{HtmlParser(text)}</>,
        },
        {
            title: 'Giá & số lượng',
            key: 'service_price',
            width: 200,
            sorter: true,
            align: 'right',
            render: (_, record) => (
                <ServicePriceCell
                    regularPrice={record.regular_price}
                    salePrice={record.sale_price}
                    quantity={record.quantity}
                    amount={record.amount}
                />
            )
        },
        {
            title: 'Ngày chốt',
            dataIndex: 'selected_at',
            key: 'selected_at',
            width: '100px',
            render: (text) => {
                if (text) {
                    return <div>{isValidDate(text, false)}</div>;
                }
                return null;
            },
        },
        {
            title: 'Nhân viên sales',
            dataIndex: 'sales',
            key: 'sales',
            width: 250,
            render: (_, record) => {
                const list = record.sales || [];
                if (list.length === 0) return null;

                return (
                    <div style={{ lineHeight: 1.3 }}>
                        {list.map((item) => (
                            <div key={item.id}>
                                <span style={{ color: '#999' }}>{item?.sales?.user_role?.name}: </span>
                                <span style={{ fontWeight: 600 }}>{item?.sales?.full_name}</span>
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: 'Bác sĩ & Phụ tá',
            key: 'medical_staff',
            width: 280,
            render: (_, record) => (
                <MedicalStaffBlock
                    doctorRoleGroups={getCustomerServiceDoctorRoleGroups(record)}
                    assistants={record.assistants}
                />
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            sorter: false,
            width: '100px',
            render: (text) => {
                if (text) {
                    return <div>{renderCustomerServiceStatus(text)}</div>;
                }
                return null;
            },
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'created_at',
            key: 'created_at',
            width: '220px',
            render: (text, record) => (
                <CreatedAtCell
                    createdAt={text}
                    user={record?.created_user}
                />
            ),
        },
        {
            title: '#',
            align: 'center',
            width: '180px',
            key: 'actions',
            render: (_, record) => {
                const isTransferred = !!record.is_transferred;

                return (
                    <>
                        <Space size={10}>
                            <Tooltip
                                title={
                                    isTransferred
                                        ? 'Không thể sửa vì đã chuyển sang dịch vụ khác'
                                        : 'Chỉnh sửa dịch vụ'
                                }
                            >
                                <Button
                                    size="small"
                                    type="dashed"
                                    className={`btn-edit ${isTransferred ? 'disabled-action' : ''}`}
                                    icon={<EditOutlined />}
                                    onClick={() => openEditForm(record.id)}
                                    disabled={!allowUpdate || isTransferred}
                                >
                                    Sửa
                                </Button>
                            </Tooltip>

                            {allowDelete && (
                                <Tooltip
                                    title={
                                        isTransferred
                                            ? 'Không thể xóa vì đã chuyển sang dịch vụ khác'
                                            : 'Xóa dịch vụ khỏi khách hàng'
                                    }
                                >
                                    <Button
                                        size="small"
                                        type="dashed"
                                        className={`btn-delete ${isTransferred ? 'disabled-action' : ''}`}
                                        icon={<DeleteOutlined />}
                                        onClick={() => openAlert([record.id])}
                                        disabled={!allowDelete || isTransferred}
                                    >
                                        Xóa
                                    </Button>
                                </Tooltip>
                            )}
                        </Space>
                    </>
                );
            },
        },
    ];

    return (
        <div>
            <div className="mt-4 mb-4">
                <Row gutter={8}>
                    <Col>
                        <Space size={8}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openAddForm}
                                disabled={!allowCreate}
                            >
                                Thêm dịch vụ chốt
                            </Button>

                            <Button
                                onClick={handleReloadData}
                                icon={<ReloadOutlined />}
                                loading={isLoading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            <div className="has-custom-column-table-btn">
                <DynamicTable
                    dataSource={services || []}
                    columns={columns}
                    isLoading={isLoading}
                    pagination={false}
                    rowSelection={false}
                    moduleKey="used_services_list"
                    buttonStyle={{ top: 6 }}
                />
            </div>

            {canViewFinance && (
                <>
                    <h6 className="mt-4">Lịch sử thanh toán</h6>
                    <CustomerReceiptsTable
                        key={`payment_history-${customer?.id}`}
                        data={customer?.receipts}
                        onViewReceipt={openReceiptDetail}
                    />
                </>
            )}

            <CustomerServicesDetail
                visible={isOpenDetail}
                currentItem={currentUsedService}
                onCancel={closeDetail}
                allowCommissionSetup={allowCommissionSetupDetail || allowCommissionSetupUpdate}
                onCommissionSetup={openCommissionSetup}
                allowCommissionRecalculate={allowCommissionRecalculate}
                onCommissionRecalculate={recalculateFromDetail}
                allowEdit={allowUpdate}
                onEdit={() => openEditForm(currentUsedService?.id)}
                onStaffChanged={handleStaffChanged}
                zIndex={editServiceId ? 999 : undefined}
            />

            <ReceiptDetail
                visible={isOpenReceiptDetail}
                currentReceipt={currentReceipt}
                reloadData={reloadReceiptDetail}
                onCancel={closeReceiptDetail}
                loadingDetail={receiptDetailLoading}
            />

            <CustomerServiceForm
                visible={isOpenCreateForm || !!editServiceId}
                customerId={Number(id)}
                currentData={null}
                customerServiceId={editServiceId}
                reloadData={handleServiceSaved}
                onCancel={closeForm}
            />

            <CustomerServiceCommissionSetupForm
                visible={!!commissionServiceId}
                customerServiceId={commissionServiceId}
                onCancel={closeCommissionSetup}
                onSaved={handleServiceSaved}
                readOnly={!allowCommissionSetupUpdate}
            />
        </div>
    );
};

export default PurchasedServices;
